import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const WhopLicense = () => {
  const navigate = useNavigate();
  const [licenseKey, setLicenseKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationStatus, setValidationStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleValidateLicense = async () => {
    if (!licenseKey.trim()) {
      toast.error("Please enter a license key");
      return;
    }

    setLoading(true);
    setValidationStatus({ type: null, message: '' });

    try {
      const response = await supabase.functions.invoke('validate-whop-license', {
        body: {
          licenseKey: licenseKey.trim(),
          preSignupValidation: true,
        },
      });

      if (response.error) {
        throw response.error;
      }

      if (response.data?.success) {
        setValidationStatus({
          type: 'success',
          message: 'License key validated! Click continue to create your account and company.'
        });
        toast.success("License key verified!");

        // Store the validated license key in localStorage for use during signup
        localStorage.setItem('whop_license_key', licenseKey.trim());
        localStorage.setItem('whop_membership_data', JSON.stringify(response.data.membership));
      } else {
        throw new Error(response.data?.message || 'Validation failed');
      }
    } catch (error: any) {
      console.error('License validation error:', error);
      setValidationStatus({
        type: 'error',
        message: error.message || 'Failed to validate license key. Please check the key and try again.'
      });
      toast.error(error.message || "Failed to validate license key");
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToSignup = () => {
    navigate('/auth?mode=signup');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Activate Your Feednatic License</CardTitle>
          <CardDescription>
            Enter your Whop license key to get started
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* License key input */}
          <div className="space-y-2">
            <Label htmlFor="licenseKey">License Key</Label>
            <Input
              id="licenseKey"
              type="text"
              placeholder="Enter your Whop license key"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !validationStatus.type) {
                  handleValidateLicense();
                }
              }}
              disabled={loading || validationStatus.type === 'success'}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              You received this key after purchasing Feednatic on the Whop marketplace
            </p>
          </div>

          {/* Validation status */}
          {validationStatus.type && (
            <Alert className={validationStatus.type === 'success'
              ? 'border-green-500/50 bg-green-500/10'
              : 'border-red-500/50 bg-red-500/10'
            }>
              {validationStatus.type === 'success' ? (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-500" />
              )}
              <AlertDescription className={validationStatus.type === 'success'
                ? 'text-green-700 dark:text-green-400'
                : 'text-red-700 dark:text-red-400'
              }>
                {validationStatus.message}
              </AlertDescription>
            </Alert>
          )}

          {/* Action buttons */}
          <div className="space-y-2">
            {validationStatus.type === 'success' ? (
              <Button
                onClick={handleContinueToSignup}
                className="w-full"
              >
                Continue to Create Account
              </Button>
            ) : (
              <Button
                onClick={handleValidateLicense}
                disabled={loading || !licenseKey.trim()}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Validating...
                  </>
                ) : (
                  'Validate License Key'
                )}
              </Button>
            )}

            {/* Link to Whop marketplace */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.open('https://whop.com', '_blank')}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Don't have a license? Visit Whop
            </Button>
          </div>

          {/* Additional info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium">What happens next?</p>
            <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
              <li>We'll verify your license key with Whop</li>
              <li>You'll create your account and company</li>
              <li>Your company will automatically be set up on the Enterprise plan</li>
              <li>Start collecting feedback immediately!</li>
            </ol>
          </div>

          {/* Help text */}
          <div className="text-center text-sm text-muted-foreground">
            <p>Need help?</p>
            <a
              href="mailto:support@feednatic.com"
              className="text-primary hover:underline"
            >
              Contact Support
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhopLicense;
